import { useTranslation } from 'react-i18next';

import styles from './Shot.module.css';

export type ShotName =
  | 'board'
  | 'calendar'
  | 'database'
  | 'database-cards'
  | 'database-kanban'
  | 'home'
  | 'phone-calendar';

const ALT = {
  board: 'landing.shots.board',
  calendar: 'landing.shots.calendar',
  database: 'landing.shots.database',
  'database-cards': 'landing.shots.databaseCards',
  'database-kanban': 'landing.shots.databaseKanban',
  home: 'landing.shots.home',
  'phone-calendar': 'landing.shots.phone',
} as const satisfies Record<ShotName, string>;

/**
 * A picture of the REAL application (a screenshot with invented data, made by
 * `e2e/landing-assets.spec.ts`), in the visitor's language. Both colour themes are in the page;
 * the stylesheet shows the one that matches the page's resolved theme (the hidden one is not exposed
 * to assistive technology either). The alt text says what the
 * picture shows, in words.
 */
export function Shot({
  name,
  eager = false,
  phone = false,
}: {
  name: ShotName;
  /** Above the fold: loaded at once instead of lazily. */
  eager?: boolean;
  phone?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage === 'ru' ? 'ru' : 'en';
  const alt = t(ALT[name]);
  // the database pictures are shot a little taller than wide-screen ones: 1440x840
  const size = phone
    ? { width: 390, height: 780 }
    : name.startsWith('database')
      ? { width: 1440, height: 840 }
      : { width: 1440, height: 900 };
  return (
    <>
      {(['light', 'dark'] as const).map((theme) => (
        <img
          key={theme}
          className={`${styles.shot} ${theme === 'light' ? styles.light : styles.dark}`}
          src={`/landing/${lang}/${name}-${theme}.jpg`}
          alt={alt}
          {...size}
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
        />
      ))}
    </>
  );
}
